/*
 * Vue Routerの $route.query で渡されたパラメータから必要な値を取得するユーティリティ
 *
 * クエリパラメータは、エンドユーザーがアドレスバーから任意の値を入れられるので、定義されてない値を取り除く
 * 数値が欲しい場合も、文字列型として来る場合があるため、必要に応じて変換を行う
 */

/**
 * クエリーパラメータが指定されたUnion型かどうかを判定し、その型の値だけを取り出した配列を返す
 * パラメータが配列でない場合も、配列にいれて返す
 *
 * @param {unknown} param 判定したい値
 * @param {(p: unknown) => p is T} check_func 値が型を満たしているかどうかを判定する関数
 * @returns {Array<T>} 取り出した配列
 */
export function queryParamDecodeToArray<T>(param: unknown, check_func: (p: unknown) => p is T): Array<T> {
    if (Array.isArray(param)) {
        return param.filter(check_func);
    }
    if (check_func(param)) {
        return [param];
    }
    return [];
}

/**
 * クエリーパラメータが指定されたUnion型かどうかを判定し、その型を返す
 * パラメータが配列で満たす値が複数ある場合は、一番最初の値を返す
 *
 * @param {unknown} param 判定したい値
 * @param {(p: unknown) => p is T} check_func 値が型を満たしているかどうかを判定する関数
 * @returns {T} 指定された型の場合はその値 そうでない場合はundefined
 */
export function queryParamDecodeToSingleValue<T>(param: unknown, check_func: (p: unknown) => p is T): T | undefined {
    const decoded = queryParamDecodeToArray(param, check_func);
    if (decoded.length > 0) {
        return decoded[0];
    } else {
        return undefined;
    }
}

/**
 * クエリーパラメータが指定されたUnion型かどうかを判定し、その型の値だけを取り出した配列を返す
 * パラメータが配列でない場合も、配列にいれて返す
 *
 * 数値のUnion型専用 比較の前にparamをparseIntで数値型に変換する
 *
 * @param {unknown} param 判定したい値
 * @param {(p: unknown) => p is T} check_func 値が型を満たしているかどうかを判定する関数
 * @returns {Array<T>} 取り出した配列
 */
export function queryParamDecodeToArrayForNumberType<T extends number>(
    param: unknown,
    check_func: (p: unknown) => p is T
): Array<T> {
    function numberDecode(val: number): Array<T> {
        if (check_func(val)) {
            return [val];
        }
        return [];
    }

    if (Array.isArray(param)) {
        return param
            .map(e => parseInt(e))
            .filter(e => !isNaN(e))
            .filter(check_func);
    }
    if (typeof param === 'string') {
        return numberDecode(parseInt(param));
    }
    if (check_func(param)) {
        return [param];
    }
    return [];
}

/**
 * クエリーパラメータが指定されたUnion型かどうかを判定し、その型を返す
 * パラメータが配列で満たす値が複数ある場合は、一番最初の値を返す
 *
 * 数値のUnion型専用 比較の前にparamをparseIntで数値型に変換する
 *
 * @param {unknown} param 判定したい値
 * @param {(p: unknown) => p is T} check_func 値が型を満たしているかどうかを判定する関数
 * @returns {T} 指定された型の場合はその値 そうでない場合はundefined
 */
export function queryParamDecodeToSingleValueForNumberType<T extends number>(
    param: unknown,
    check_func: (p: unknown) => p is T
): T | undefined {
    const decoded = queryParamDecodeToArrayForNumberType(param, check_func);
    if (decoded.length > 0) {
        return decoded[0];
    } else {
        return undefined;
    }
}
